import React, { useState } from 'react';

/**
 * TeamLogo Component
 * Displays team logo with configurable background
 * 
 * @param {Object} team - Team data { logo_url, short_name, name, primary_color }
 * @param {string} size - Tailwind size class (default: 'w-16 h-16')
 * @param {Object} logoStyle - Style config { backgroundStyle, backgroundColor }
 *   - If null, uses simple square style (for operator views)
 * @param {boolean} showInitials - Whether to show initials fallback (default: true)
 * @param {string} shape - 'circle' or 'square' (default: 'circle')
 */
export const TeamLogo = ({ 
  team, 
  size = 'w-16 h-16', 
  logoStyle = null,
  showInitials = true,
  shape = 'circle'
}) => {
  const [imageError, setImageError] = useState(false);

  if (!team) {
    return showInitials ? (
      <div className={`${size} bg-gray-500 ${shape === 'circle' ? 'rounded-full' : 'rounded-lg'} flex items-center justify-center text-white font-bold text-lg`}>
        ?
      </div>
    ) : null;
  }

  // Get team initials (first 3 letters of short_name or name)
  const getInitials = () => {
    const name = team.short_name || team.name || '?';
    return name.substring(0, 3).toUpperCase();
  };

  // Determine background class for OBS overlays
  const getBackgroundClass = () => {
    if (!logoStyle) return ''; // No background for operator views

    switch (logoStyle.backgroundStyle) {
      case 'white':
        return 'bg-white';
      case 'dark':
        return 'bg-gray-900';
      case 'transparent':
        return ''; // Truly transparent
      case 'custom':
        return ''; // Will use inline style
      default:
        return '';
    }
  };

  const getBackgroundStyle = () => {
    if (logoStyle?.backgroundStyle === 'custom' && logoStyle?.backgroundColor) {
      return { backgroundColor: logoStyle.backgroundColor };
    }
    return {};
  };

  // OPERATOR VIEW (Current Score, Watch) - Simple square logo, no background
  if (!logoStyle) {
    if (team.logo_url && !imageError) {
      return (
        <img 
          src={team.logo_url}
          alt={team.short_name || team.name}
          className={`${size} object-contain`}
          onError={() => setImageError(true)}
        />
      );
    }

    // Fallback to initials (square with team color)
    if (showInitials) {
      return (
        <div 
          className={`${size} rounded-lg flex items-center justify-center text-white font-bold text-lg`}
          style={{ backgroundColor: team.primary_color || '#6B7280' }}
        >
          {getInitials()}
        </div>
      );
    }

    return null;
  }

  // OBS OVERLAY VIEW - Circular with background
  if (team.logo_url && !imageError) {
    const containerClass = `${size} ${getBackgroundClass()} ${shape === 'circle' ? 'rounded-full' : 'rounded-lg'} flex items-center justify-center overflow-hidden`;
    
    return (
      <div 
        className={containerClass}
        style={getBackgroundStyle()}
      >
        <img 
          src={team.logo_url}
          alt={team.short_name || team.name}
          className="w-full h-full object-contain p-2"
          style={{ 
            // Ensure PNG transparency is respected
            backgroundColor: 'transparent'
          }}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // Fallback to initials
  if (showInitials) {
    return (
      <div 
        className={`${size} ${shape === 'circle' ? 'rounded-full' : 'rounded-lg'} flex items-center justify-center text-white font-bold text-lg`}
        style={{ backgroundColor: team.primary_color || '#6B7280' }}
      >
        {getInitials()}
      </div>
    );
  }

  return null;
};

export default TeamLogo;
